import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import { styled } from "styled-components";
import { fromUnixTime, getWeek } from "date-fns";

// Custom status colors
const COLOR_OPEN = "#ACB7BD";
const COLOR_BOOKED = "#cce5c6";
const COLOR_LATE_CANCELED = "#f9d5aa";
const COLOR_CANCELED = "#f9e79d";
const COLOR_NO_SHOW = "#FDC7BC";
const COLOR_NO_SHOW_ORGANIZER = "#c7e1f5";
const COLOR_NO_SHOW_ALL = "#f3cece";

// Styled components
const WeeksTotalContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const Title = styled.span`
  font-family: "InterDisplay";
  font-weight: 500;
  font-size: 18px;
  margin: 0;
  margin-bottom: 10px;
  color: ${({ theme }) => theme.COLORS.GRAY_BLACK};
`;

const TotalSlots = styled.span`
  font-weight: 400;
  font-size: 16px;
`;

const DetailWrapper = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  column-gap: 50px;
`;

const InfoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  line-height: 26px;
  margin-bottom: 8px;
  span {
    color: ${({ theme }) => theme.COLORS.GRAY_BLACK};
  }
`;

const StatusNames = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.COLORS.GRAY_BLACK};
`;

const StatusColorWrapper = styled.div`
  border-radius: 24px;
  padding: 0px 8px;
`;

const OpenStatus = styled(StatusColorWrapper)`
  background: ${COLOR_OPEN};
`;

const BookedStatus = styled(StatusColorWrapper)`
  background: ${COLOR_BOOKED};
`;

const CanceledStatus = styled(StatusColorWrapper)`
  background: ${COLOR_CANCELED};
`;

const LateCanceledStatus = styled(StatusColorWrapper)`
  background: ${COLOR_LATE_CANCELED};
`;

const NoShowStatus = styled(StatusColorWrapper)`
  background: ${COLOR_NO_SHOW};
`;

const NoShowOrganizerStatus = styled(StatusColorWrapper)`
  background: ${COLOR_NO_SHOW_ORGANIZER};
`;

const NoShowAllStatus = styled(StatusColorWrapper)`
  background: ${COLOR_NO_SHOW_ALL};
`;

const renderDataValue = (count, percentage, testid = "undefined") => {
  if (count === null || percentage === null) return <span>{"-(-%)"}</span>;
  return (
    <span data-testid={testid}>{`${count} (${Math.round(percentage)}%)`}</span>
  );
};

const DataRow = ({ overviewData, dateTo, dateFrom, totalCount, t }) => {
  return (
    <div>
      <WeeksTotalContainer>
        <Title>
          {`${t("WEEK")} ${getWeek(fromUnixTime(dateFrom), { weekStartsOn: 1 })}-${getWeek(fromUnixTime(dateTo), { weekStartsOn: 1 })}`}
        </Title>
        <div>
          <Title>
            {t("TOTAL")}&nbsp;
            <TotalSlots>{totalCount}</TotalSlots>
          </Title>
        </div>
      </WeeksTotalContainer>
      <DetailWrapper>
        <InfoWrapper>
          <StatusNames>{t("FREE")}&nbsp;</StatusNames>
          <OpenStatus>
            {renderDataValue(
              overviewData?.OPEN.count ? overviewData.OPEN.count : 0,
              overviewData?.OPEN.percentage ? overviewData.OPEN.percentage : 0,
              "freeCount-label",
            )}
          </OpenStatus>
        </InfoWrapper>
        <InfoWrapper>
          <StatusNames>{t("BOOKED_MULTIPLE")}&nbsp;</StatusNames>
          <BookedStatus>
            {renderDataValue(
              overviewData?.BOOKED.count ? overviewData.BOOKED.count : 0,
              overviewData?.BOOKED.percentage
                ? overviewData.BOOKED.percentage
                : 0,
              "bookedCount-label",
            )}
          </BookedStatus>
        </InfoWrapper>
        <InfoWrapper>
          <StatusNames>{t("CANCELED")}&nbsp;</StatusNames>
          <CanceledStatus>
            {renderDataValue(
              overviewData?.CANCELED.count ? overviewData.CANCELED.count : 0,
              overviewData?.CANCELED.percentage
                ? overviewData.CANCELED.percentage
                : 0,
              "canceledCount-label",
            )}
          </CanceledStatus>
        </InfoWrapper>
        <InfoWrapper>
          <StatusNames>{t("LATE_CANCEL")}&nbsp;</StatusNames>
          <LateCanceledStatus>
            {renderDataValue(
              overviewData?.LATE_CANCELED.count
                ? overviewData.LATE_CANCELED.count
                : 0,
              overviewData?.LATE_CANCELED.percentage
                ? overviewData.LATE_CANCELED.percentage
                : 0,
              "lateCancelCount-label",
            )}
          </LateCanceledStatus>
        </InfoWrapper>
        <InfoWrapper>
          <StatusNames>{t("NO_SHOW")}&nbsp;</StatusNames>
          <NoShowStatus>
            {renderDataValue(
              overviewData?.NO_SHOW.count ? overviewData.NO_SHOW.count : 0,
              overviewData?.NO_SHOW.percentage
                ? overviewData.NO_SHOW.percentage
                : 0,
              "noShowCount-label",
            )}
          </NoShowStatus>
        </InfoWrapper>
        <InfoWrapper>
          <StatusNames>{t("ORGANIZER_NO_SHOW")}&nbsp;</StatusNames>
          <NoShowOrganizerStatus>
            {renderDataValue(
              overviewData?.ORGANIZER_NO_SHOW.count
                ? overviewData.ORGANIZER_NO_SHOW.count
                : 0,
              overviewData?.ORGANIZER_NO_SHOW.percentage
                ? overviewData.ORGANIZER_NO_SHOW.percentage
                : 0,
              "organizerNoShowCount-label",
            )}
          </NoShowOrganizerStatus>
        </InfoWrapper>
        <InfoWrapper>
          <StatusNames>{t("NO_SHOW_ALL")}&nbsp;</StatusNames>
          <NoShowAllStatus>
            {renderDataValue(
              overviewData?.NO_SHOW_ALL.count
                ? overviewData.NO_SHOW_ALL.count
                : 0,
              overviewData?.NO_SHOW_ALL.percentage
                ? overviewData.NO_SHOW_ALL.percentage
                : 0,
              "noShowAllCount-label",
            )}
          </NoShowAllStatus>
        </InfoWrapper>
      </DetailWrapper>
    </div>
  );
};

DataRow.propTypes = {
  overviewData: PropTypes.object,
  dateTo: PropTypes.any,
  dateFrom: PropTypes.any,
  totalCount: PropTypes.any,
  t: PropTypes.func,
};

export default withTranslation()(DataRow);
